import {
  collection,
  onSnapshot,
  orderBy,
  query,
  Unsubscribe
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { apiClient } from './apiClient';
import { CannedReply, CannedReplyInput } from '../../types/models/whatsapp';

const CANNED_REPLIES = 'whatsapp_canned_replies';

/**
 * Canned replies follow the same split as the rest of the module: live reads
 * straight from Firestore, writes through the API so validation and ownership
 * checks stay server-side.
 */
class CannedReplyService {
  private map(id: string, data: any): CannedReply {
    return {
      id,
      shortcut: data.shortcut || '',
      title: data.title || '',
      body: data.body || '',
      category: data.category || 'General',
      scope: data.scope === 'personal' ? 'personal' : 'team',
      createdBy: data.createdBy || '',
      createdByName: data.createdByName || '',
      useCount: data.useCount || 0,
      createdAt: data.createdAt || 0,
      updatedAt: data.updatedAt || 0
    };
  }

  /**
   * Live list, already filtered to what this agent may use: team replies plus
   * their own personal ones. The same filter is applied server-side on the
   * REST path; this one keeps the Firestore read consistent with it.
   */
  subscribe(
    agentId: string | null,
    onChange: (replies: CannedReply[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    if (!db) {
      onError?.(new Error('Firestore unavailable'));
      return () => {};
    }

    const q = query(
      collection(db, CANNED_REPLIES),
      orderBy('useCount', 'desc')
    );

    return onSnapshot(
      q,
      (snapshot) => {
        const replies = snapshot.docs
          .map((d) => this.map(d.id, d.data()))
          .filter(
            (reply) => reply.scope === 'team' || reply.createdBy === agentId
          );
        onChange(replies);
      },
      (error) => {
        console.error('❌ Canned reply subscription error:', error);
        onError?.(error as Error);
      }
    );
  }

  async list(): Promise<CannedReply[]> {
    const response = await apiClient.get<{ replies: CannedReply[] }>(
      '/canned-replies'
    );
    return response.replies;
  }

  async create(input: CannedReplyInput): Promise<{ id: string }> {
    return apiClient.post<{ id: string }>('/canned-replies', input);
  }

  async update(id: string, input: Partial<CannedReplyInput>): Promise<void> {
    await apiClient.patch(`/canned-replies/${id}`, input);
  }

  async remove(id: string): Promise<void> {
    await apiClient.delete(`/canned-replies/${id}`);
  }

  /** Best-effort usage counter — never awaited on the send path. */
  recordUse(id: string): void {
    apiClient.post(`/canned-replies/${id}/used`).catch(() => {});
  }
}

export const cannedReplyService = new CannedReplyService();
export default cannedReplyService;
